import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { Button, Card, EmptyState, FormField, Input, Modal, MoneyText, Skeleton, toast } from '@/ui';
import {
  formatMoney,
  totalAmount,
  totalOf,
  unitRate,
  OFFER_PRICE_FRACTION_DIGITS,
  RATE_FRACTION_DIGITS,
} from '@/lib/money';
import { isAmountInRange, parseRate } from '@/lib/task-amount';
import { exceedsDeviation, rateDeviation, referenceRateFor } from '@/lib/offer-rate';
import { pairOf } from '@/lib/task-currency';
import { findAccountsForExchange } from '@/lib/task-accounts';
import { useSessionStore } from '@/auth/session.store';
import { hasPermission } from '@/auth/types';
import { useUpsertOffer } from '@/features/offers/queries';
import { exchangeOffersHref, wonOfferTabFor } from '@/features/offers/tab-params';
import { useRates } from '@/features/rates/useRates';
import { useCachedTask, useTask, useTaskAccounts } from './queries';
import { counterpartyName, type TaskRow } from './types';
import { marketOutcomeView, marketTaskPollMs, type MarketOutcomeView } from './market-outcome';
import { offerLastWriterName, offerOwnership } from './offer-ownership';
import { BankPanel, InfoGrid, QuoteCard, QuoteHeader, type InfoItem } from './components/QuoteCard';
import { useCountdown } from './components/useCountdown';
import { taskErrorMessage } from './error-message';
import styles from './MarketTaskDetail.module.css';

/**
 * PIYASA — TEK TALEP + TEKLIF VERME.
 *
 * Menude yer almaz; piyasa listesinden ve derin baglantidan gelinir.
 *
 * ============================ TEK TEKLIF KURALI =============================
 * Bir buro (organizasyon) bir talebe EN FAZLA BIR canli teklif verir (2026-10-03; eskiden
 * kural kisi basinaydi ve ayni buronun iki elemani ayri teklif verebiliyordu). Bu yuzden
 * "buro zaten teklif verdi" durumu ikinci bir olusturmaya degil DUZENLEMEYE cikar — form mevcut
 * fiyatla dolu acilir ve dugme "Guncelle" der. Ayni uc (POST /v1/offers) ikisini de karsiliyor.
 * Teklif is arkadasininsa (./offer-ownership.ts) etiket "Buronuzun teklifi", dugme "Buronun
 * teklifini guncelle"; `editOffer` yoksa dugme yerine not (sunucu 403 ORGANIZATION_OFFER_EXISTS).
 *
 * ========================= KARAR ANINDA GUNCELLEME (2026-10-02) =====================
 * Web'e push yok. Talep canliyken (teklif/karar fazinda 'new') satir 5 sn'de bir yoklanir
 * (market-outcome.ts marketTaskPollMs); sunucunun `marketOutcome`'u gelince yoklama durur ve
 * teklif dugmesinin yerine SONUC KARTI cikar: Kazandiniz (+ Tekliflerim -> Bekleyenler),
 * Buronuz kazandi / Baska buroya verildi / Kapandi / Iptal edildi / Suresi doldu (yalniz bilgi).
 * Teklif formu o an aciksa kapanir ve kartta kisa bir not kalir. Eskiden detay yoklanmiyordu
 * ve karardan sonra yalniz "teklif toplama suresi doldu" diyordu.
 */
export function MarketTaskDetailPage() {
  const { t } = useTranslation();
  const params = useParams<{ taskId: string }>();
  const taskId = params.taskId ?? '';

  const seed = useCachedTask(taskId);
  // Buro gorunumu: eslesme parametresi `taskPretenderCurrencyUserId` (MarketPage ile ayni).
  // Olmadan sunucu en dar okumaya duser ve derin baglanti hep bos doner.
  const userId = useSessionStore((state) => state.snapshot?.user.id ?? '');
  const ownership = useMemo(
    () => (userId ? { taskPretenderCurrencyUserId: userId } : {}),
    [userId],
  );
  const query = useTask(taskId, seed, ownership, marketTaskPollMs);
  const task = query.data ?? null;

  return (
    <section className={styles.page}>
      <div className={styles.back}>
        <Link className={styles.backLink} to="/buro/piyasa">
          <ArrowLeft size={16} aria-hidden="true" />
          {t('task.backToMarket')}
        </Link>
      </div>

      {query.isPending && !task ? (
        <Skeleton shape="block" height="220px" />
      ) : task === null ? (
        <Card padding="lg">
          <EmptyState
            tone="error"
            title={t('task.notFound')}
            description={t('task.notFoundDetail')}
            action={
              <Button tone="neutral" onClick={() => { query.refetch().catch(() => undefined); }}>
                {t('common.retry')}
              </Button>
            }
          />
        </Card>
      ) : (
        <TaskDetail task={task} receivedAtMs={query.dataUpdatedAt} />
      )}
    </section>
  );
}

function TaskDetail({ task, receivedAtMs }: { task: TaskRow; receivedAtMs: number }) {
  const { t } = useTranslation();
  const snapshot = useSessionStore((state) => state.snapshot);
  // Satirdaki teklif kimin (yukaridaki sorgu parametresi `ownership` ile karistirma).
  const offerOwner = offerOwnership(task, snapshot?.user.id);
  const alreadyBid = offerOwner !== 'none';
  const colleagueOffer = offerOwner === 'colleague';
  const lastWriter = offerLastWriterName(task, snapshot?.user.id);
  // Yeni teklif `createOffer`, mevcudu (is arkadasininki dahil) degistirmek `editOffer` ister.
  const canAct = hasPermission(snapshot, alreadyBid ? 'editOffer' : 'createOffer');
  const [formOpen, setFormOpen] = useState(false);
  // Karar sonucu; talep acikken null.
  const outcome = marketOutcomeView(task.marketOutcome);
  // Form acikken karar geldi: form artik CIZILMEZ (asagida) ve sonuc kartina not duser. Durum
  // effect'le sifirlanmiyor: sonuc varken teklif dugmesi de yok, yani "form acik + sonuc var"
  // ancak bu yolla olusur.
  const formClosedByOutcome = formOpen && outcome !== null;

  const phase = useCountdown({
    taskStatusCode: task.taskStatusCode,
    // Secilmeyen buro icin musterinin odeme sayaci ilgisiz: kart kapali cizilir (M1).
    countdownStatus: outcome?.closesPhase ? 'passive' : task.countdownStatus,
    statusUpdatedAt: task.statusUpdatedAt,
    countdown: task.countdown,
    receivedAtMs,
  });

  const pair = pairOf(task.taskTypeCode);
  const name = counterpartyName(task.userBusinessName, task.userFullName);

  const info: InfoItem[] = [];
  if (task.offerPrice !== null) {
    const rate = unitRate(task.offerPrice);
    info.push({
      label: colleagueOffer ? t('task.orgOfferPrice') : t('task.myOfferPrice'),
      rate,
      currency: pair.secondCurrency,
      emphasis: true,
    });
    info.push({
      label: t('labels.total'),
      total: totalOf(rate, task.taskAmount),
      currency: pair.secondCurrency,
    });
  }
  if (lastWriter !== null) info.push({ label: t('task.offerLastUpdatedBy'), text: lastWriter });
  info.push({
    label: task.userBusinessName ? t('labels.company') : t('labels.name'),
    text: name ?? '',
  });

  return (
    <>
      <QuoteCard phase={phase} won={outcome?.won ?? false} lost={outcome?.lost ?? false}>
        <QuoteHeader
          id={task.id}
          taskTypeCode={task.taskTypeCode}
          isBuying={task.taskIsBuying}
          amount={totalAmount(task.taskAmount)}
          amountCurrency={pair.currency}
          createdAt={task.createdAt}
          phase={phase}
          // Kapali cizilen sonucta durum etiketi ("Beklemede") yazilmaz: teklif vermeyen buroya
          // secim yapildigini soylerdi (K3).
          statusCode={outcome?.closesPhase ? undefined : task.taskStatusCode}
          badgeLabel={outcome ? t(outcome.labelKey) : undefined}
        />
        <InfoGrid items={info} />
        <BankPanel account={task.taskData?.selectedAccount} />
      </QuoteCard>

      {outcome ? (
        <MarketResult
          outcome={outcome}
          offersHref={exchangeOffersHref(wonOfferTabFor(task.taskStatusCode))}
          formClosed={formClosedByOutcome}
        />
      ) : phase.phase !== 'offer' ? (
        // Karar henuz yok ama teklif suresi doldu (karar fazi). Suresi dolan talep 'expired'
        // sonucuyla gelir ve ayni metni sonuc kartinda gosterir.
        <p className={styles.notice}>{t('task.offerWindowClosed')}</p>
      ) : !canAct ? (
        <p className={styles.notice}>
          {colleagueOffer
            ? t('errors.organizationOfferExists')
            : t('permissions.noPermissionOffer')}
        </p>
      ) : (
        <Button
          block
          onClick={() => {
            setFormOpen(true);
          }}
        >
          {colleagueOffer
            ? t('offerFlow.updateOrgOffer')
            : alreadyBid
              ? t('offerFlow.update')
              : t('offerFlow.create')}
        </Button>
      )}

      {formOpen && outcome === null ? (
        <OfferForm
          task={task}
          onClose={() => {
            setFormOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * KARAR SONUCU — teklif dugmesinin yerine. Baslik Gecmis kartindaki rozetle ayni metin
 * (market-outcome.ts). Kazanan icin Tekliflerim baglantisi: yeni kazanilan is Bekleyenler'de
 * (ilk adim "Odeme alindi"), sonraki durumlarda isin bulundugu sekme. Digerleri yalniz bilgi.
 */
function MarketResult({
  outcome,
  offersHref,
  formClosed,
}: {
  outcome: MarketOutcomeView;
  offersHref: string;
  formClosed: boolean;
}) {
  const { t } = useTranslation();
  return (
    <section
      className={[styles.result, outcome.won ? styles.resultWon : undefined]
        .filter(Boolean)
        .join(' ')}
      role="status"
    >
      <p className={styles.resultTitle}>{t(outcome.labelKey)}</p>
      <p className={styles.resultBody}>{t(outcome.detailKey)}</p>
      {formClosed ? (
        <p className={styles.resultBody}>{t('task.marketResult.formClosed')}</p>
      ) : null}
      {outcome.won ? (
        <Link className={styles.resultLink} to={offersHref}>
          {t('task.marketResult.goToOffers')}
        </Link>
      ) : null}
    </section>
  );
}

/**
 * Teklif formu.
 *
 * Iki asama: fiyat girisi ve ONAY. Onay adimi bilincli bir surtunme — teklif
 * bir para taahhudu ve tek tiklamayla gonderilmemeli. Ozet, islemi, miktari,
 * BIRIM KURU ve HESAPLANMIS TOPLAMI birlikte gosterir; carpimi kullanicinin
 * kafasinda yapmasi gereken yer tam olarak burasi.
 */
function OfferForm({ task, onClose }: { task: TaskRow; onClose: () => void }) {
  const { t } = useTranslation();
  const userId = useSessionStore((state) => state.snapshot?.user.id);
  const accountsQuery = useTaskAccounts(userId);
  const upsert = useUpsertOffer();
  // TCMB referansi yalniz UYARI icin (engellemez). Bulten gelmediyse uyari da yok.
  const rates = useRates();

  const [priceText, setPriceText] = useState(
    task.offerPrice === null ? '' : String(task.offerPrice).replace('.', ','),
  );
  const [error, setError] = useState<string | undefined>(undefined);
  const [confirming, setConfirming] = useState(false);

  const pair = pairOf(task.taskTypeCode);
  // KUR okumasi `parseAmount` DEGIL: "34.567" orada 34567 olurdu (bkz. task-amount.ts).
  const parsedRate = parseRate(priceText);
  const parsed = parsedRate !== null && 'value' in parsedRate ? parsedRate.value : null;

  // Takas ayni kurumdan yurur: buronun, MUSTERININ sectigi bankada ve dogru
  // para biriminde hesabi olmali. Yoksa teklif gonderilemez.
  const eligible = findAccountsForExchange(
    accountsQuery.data ?? [],
    task.taskTypeCode,
    task.taskIsBuying,
    task.taskData?.selectedAccount?.accountCode,
  );

  const review = (): void => {
    if (parsedRate !== null && 'error' in parsedRate) {
      setError(t('offerFlow.amountTooPrecise'));
      return;
    }
    if (parsed === null) {
      setError(t('offerFlow.amountInvalid'));
      return;
    }
    if (!isAmountInRange(parsed)) {
      setError(t('offerFlow.amountTooLarge'));
      return;
    }
    if (eligible.length === 0) {
      setError(t('offerFlow.createFailed'));
      return;
    }
    setError(undefined);
    setConfirming(true);
  };

  const submit = (): void => {
    const account = eligible[0];
    if (parsed === null || !account || !userId) return;
    upsert.mutate(
      {
        offerUserId: userId,
        taskId: task.id,
        // BIRIM KUR gonderiliyor — toplam DEGIL. Sunucu da boyle bekliyor.
        offerPrice: parsed,
        offerData: { selectedAccount: account },
      },
      {
        onSuccess: () => {
          toast({ message: t('task.offerSaved'), tone: 'success' });
          onClose();
        },
        onError: (error) => {
          setConfirming(false);
          // Sunucu sebebi soyluyor (TASK_NOT_OPEN_FOR_OFFERS, PERMISSION_DENIED,
          // ORGANIZATION_OFFER_EXISTS, ACCOUNT_LEG_MISMATCH...); eslenmeyen kod eski genel
          // metne duser.
          setError(taskErrorMessage(error, t, 'offerFlow.createFailed'));
        },
      },
    );
  };

  if (confirming && parsed !== null) {
    const rate = unitRate(parsed);
    const reference = referenceRateFor(rates.rows, task.taskTypeCode);
    const deviation = rateDeviation(parsed, reference);
    const warnDeviation = exceedsDeviation(parsed, reference);
    return (
      <Modal
        open
        onClose={() => {
          setConfirming(false);
        }}
        title={t('task.confirmTitle')}
        width="sm"
        dismissOnScrim={!upsert.isPending}
        footer={
          <>
            <Button
              tone="ghost"
              disabled={upsert.isPending}
              onClick={() => {
                setConfirming(false);
              }}
            >
              {t('actions.cancel')}
            </Button>
            <Button loading={upsert.isPending} onClick={submit}>
              {t('actions.confirm')}
            </Button>
          </>
        }
      >
        <dl className={styles.summary}>
          <div className={styles.summaryRow}>
            <dt>{t('labels.transaction')}</dt>
            <dd>{`${pair.code} ${t(task.taskIsBuying ? 'labels.buy' : 'labels.sell')}`}</dd>
          </div>
          <div className={styles.summaryRow}>
            <dt>{t('labels.amount')}</dt>
            <dd>
              <MoneyText total={totalAmount(task.taskAmount)} currency={pair.currency} />
            </dd>
          </div>
          <div className={styles.summaryRow}>
            <dt>{t('labels.unitRate')}</dt>
            <dd>
              {/* Kullanicinin girdigi TEKLIF fiyati: numeric(11,2). */}
              <MoneyText
                rate={rate}
                fractionDigits={OFFER_PRICE_FRACTION_DIGITS}
                currency={pair.secondCurrency}
              />
            </dd>
          </div>
          <div className={[styles.summaryRow, styles.summaryTotal].join(' ')}>
            <dt>{t('labels.total')}</dt>
            <dd>
              <MoneyText
                total={totalOf(rate, task.taskAmount)}
                currency={pair.secondCurrency}
                size="h3"
                tone="primary"
              />
            </dd>
          </div>
        </dl>
        {/* ENGELLEMEYEN uyari: onay dugmesi aktif kalir. Esik urun karari (lib/offer-rate.ts). */}
        {warnDeviation && reference !== null && deviation !== null ? (
          <p className={styles.warningNotice} role="status">
            {t('offerFlow.rateDeviationWarn', {
              reference: `${formatMoney(reference, RATE_FRACTION_DIGITS)} ${pair.secondCurrency}`,
              percent: Math.round(deviation * 100),
            })}
          </p>
        ) : null}
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`${pair.code} ${t(task.taskIsBuying ? 'labels.buy' : 'labels.sell')}`}
      width="sm"
    >
      <div className={styles.form}>
        <FormField
          label={`${t('labels.unitRate')} ${pair.secondCurrency}`}
          hint={t('task.unitRateHint')}
          error={error}
        >
          <Input
            numeric
            autoFocus
            inputMode="decimal"
            value={priceText}
            onChange={(event) => {
              setPriceText(event.target.value);
            }}
          />
        </FormField>

        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>{t('labels.amount')}</span>
          <MoneyText total={totalAmount(task.taskAmount)} currency={pair.currency} />
        </div>

        <div className={styles.actions}>
          <Button tone="ghost" onClick={onClose}>
            {t('actions.cancel')}
          </Button>
          <Button onClick={review}>{t('actions.confirm')}</Button>
        </div>
      </div>
    </Modal>
  );
}
